import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import {
  FOOTER_HEIGHT,
  GRAY,
  HEADER_HEIGHT,
  LINE_WIDTH,
  LOGO_HEIGHT,
  LOGO_WIDTH,
  PADDING,
  PAGE_HEIGHT,
  PAGE_MARGIN,
  PAGE_WIDTH,
  TEXT_LINE_HEIGHT,
} from '@shared/constants/product-page-print-constants';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import {
  addBrandAndDescription,
  addFooter,
  addHeader,
  addImage,
  addIngredientsAllergensAndNutritionals,
  addPreparationAndCooking,
  addPricesAndInventories,
  addProductClaims,
  addProductHighlights,
  addProductImages,
  addProductNumbersAndPackSize,
  addProductPills,
  addShippingInfo,
} from '@product-detail/utils/product-detail-print.util';
import {
  getOrderedContextKeys,
  getPriorityOrders,
} from '@product-detail/utils/product-images.util';
import { MerchPrintModalComponent } from '@shared/components/merch-print-modal/merch-print-modal.component';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { CONTENT_TYPE_PDF } from '@shared/constants/files';
import { SentenceCasePipe } from '@shared/pipes/sentence-case.pipe';
import { FileHandlerBaseService } from '@shared/services/file-handler/file-handler-base.service';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { ServiceHandlerService } from '@shared/services/service-handler.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { ProductImageBase64Response } from '@usf/product-types';
import jsPDF from 'jspdf';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ProductDetailPrintService extends FileHandlerBaseService {
  includeProductPrices = false;

  constructor(
    toastService: ToastService,
    public translateService: TranslateService,
    public platform: Platform,
    public datePipe: DatePipe,
    public sentenceCasePipe: SentenceCasePipe,
    public loadingSpinnerService: LoadingSpinnerService,
    readonly platformService: PlatformService,
    private modalController: ModalController,
    private modalService: ModalService,
    private serviceHandler: ServiceHandlerService,
    private productAnalyticsService: ProductAnalyticsService,
  ) {
    super(
      toastService,
      translateService,
      platform,
      datePipe,
      sentenceCasePipe,
      loadingSpinnerService,
    );
  }

  async showPrintModal(product: ProductDetailViewModel) {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: MerchPrintModalComponent,
        rootPageParams: { title: 'i18n.productDetail.printModal.title' },
      },
      'modal-desktop-size-sm',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();

    await modal.onDidDismiss().then((resp: any) => {
      if (!!resp?.data && resp?.data?.includeProductPrices !== undefined) {
        this.includeProductPrices = resp?.data?.includeProductPrices;
        this.printProductDetail(product);
      }
    });
  }

  async printProductDetail(product: ProductDetailViewModel) {
    this.loadingSpinnerService
      .createSpinnerModal()
      .then(async () => {
        const fileName = product?.summary?.productDescTxtl ?? 'Product Detail';
        const fileData = await this.createPDFBlob(product, fileName);

        const validatedFileName = `${fileName
          ?.replace(/[/\\.?%*:|"<>]/g, '_')
          ?.trim()}.pdf`;
        const isMobile = Capacitor.isNativePlatform();
        if (isMobile) {
          this.downloadFile(
            fileData,
            validatedFileName,
            CONTENT_TYPE_PDF,
            true,
            false,
          );
        } else {
          this.printWeb(fileData, CONTENT_TYPE_PDF);
        }
        this.productAnalyticsService.trackPrintPDP();
      })
      .finally(() => {
        this.cleanUp();
      });
  }

  async createPDFBlob(product: ProductDetailViewModel, fileName: string) {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter',
    });
    doc.setProperties({ title: fileName });
    doc.setLineHeightFactor(TEXT_LINE_HEIGHT);

    const productImages = await this.getProductImages(product);
    const primaryProductImage = productImages?.shift();

    let [currentX, currentY] = await this.createFirstPageHeader(
      doc,
      product,
      primaryProductImage,
    );
    [currentX, currentY] = this.createProductHighlights(
      doc,
      product,
      currentX,
      currentY,
    );
    [currentX, currentY] = this.createProductClaims(
      doc,
      product,
      currentX,
      currentY,
    );
    [currentX, currentY] = this.createPreparationAndCooking(
      doc,
      product,
      currentX,
      currentY,
    );
    this.createShippingInfo(doc, product, currentX, currentY);

    await this.createIngredientsAllergensAndNutritionals(doc, product);

    this.createProductImages(doc, productImages);

    this.createHeaders(doc, product);
    this.createFooters(doc);

    return doc.output('blob');
  }

  private async createFirstPageHeader(
    doc: jsPDF,
    product: ProductDetailViewModel,
    productImage: string,
  ) {
    let currentX = PAGE_MARGIN;
    let currentY = PAGE_MARGIN;

    currentX = addImage(
      doc,
      '../assets/images/usfoods-logo-print.webp',
      'JPEG',
      currentX,
      currentY,
      LOGO_WIDTH,
      LOGO_HEIGHT,
    )[0];

    const productImageWidth = 168;
    const productImageHeight = 150;
    let bottomOfProductImageY = 0;
    currentX += 36;
    if (!!productImage) {
      [currentX, bottomOfProductImageY] = addImage(
        doc,
        productImage,
        'JPEG',
        currentX,
        currentY,
        productImageWidth,
        productImageHeight,
      );
    } else {
      [currentX, bottomOfProductImageY] = addImage(
        doc,
        '../assets/icon/restaurant.png',
        productImage,
        currentX,
        currentY,
        productImageWidth,
        productImageHeight,
      );
    }

    currentX += 36;
    [currentX, currentY] = addBrandAndDescription(
      doc,
      product,
      currentX,
      currentY,
    );

    currentY += PADDING;
    [currentX, currentY] = addProductNumbersAndPackSize(
      doc,
      product,
      currentX,
      currentY,
    );

    currentY += PADDING;
    [currentX, currentY] = addPricesAndInventories(
      doc,
      product?.productNumber,
      this.includeProductPrices,
      this.translateService,
      currentX,
      currentY,
    );

    [currentX, currentY] = await addProductPills(
      doc,
      product,
      currentX,
      currentY,
      this.serviceHandler,
    );

    return [currentX, Math.max(bottomOfProductImageY, currentY)];
  }

  private createProductHighlights(
    doc: jsPDF,
    product: ProductDetailViewModel,
    currentX: number,
    currentY: number,
  ) {
    currentX = PAGE_MARGIN;
    currentY += PADDING;

    return addProductHighlights(
      doc,
      product,
      this.includeProductPrices,
      this.translateService,
      currentX,
      currentY,
    );
  }

  private createProductClaims(
    doc: jsPDF,
    product: ProductDetailViewModel,
    currentX: number,
    currentY: number,
  ) {
    currentX = PAGE_MARGIN;

    if (doc.getCurrentPageInfo().pageNumber === 1) {
      doc.addPage();
      currentY = HEADER_HEIGHT;
    } else {
      doc.setDrawColor(GRAY);
      doc.setLineWidth(LINE_WIDTH);
      doc.line(currentX, currentY, PAGE_WIDTH - PAGE_MARGIN, currentY);
      currentY += LINE_WIDTH;
    }

    return addProductClaims(
      doc,
      product,
      this.translateService,
      currentX,
      currentY,
    );
  }

  private createPreparationAndCooking(
    doc: jsPDF,
    product: ProductDetailViewModel,
    currentX: number,
    currentY: number,
  ) {
    currentX = PAGE_MARGIN;

    return addPreparationAndCooking(
      doc,
      product,
      this.translateService,
      currentX,
      currentY,
    );
  }

  private createShippingInfo(
    doc: jsPDF,
    product: ProductDetailViewModel,
    currentX: number,
    currentY: number,
  ) {
    currentX = PAGE_MARGIN;

    return addShippingInfo(
      doc,
      product,
      this.translateService,
      currentX,
      currentY,
    );
  }

  private async createIngredientsAllergensAndNutritionals(
    doc: jsPDF,
    product: ProductDetailViewModel,
  ) {
    doc.addPage();
    const currentX = PAGE_MARGIN;
    const currentY = HEADER_HEIGHT;

    await addIngredientsAllergensAndNutritionals(
      doc,
      product,
      this.translateService,
      currentX,
      currentY,
    );
  }

  private createProductImages(doc: jsPDF, productImages: string[]) {
    if (productImages?.length > 0) {
      doc.addPage();
      const currentX = PAGE_MARGIN + 90;
      const currentY = HEADER_HEIGHT + PADDING * 2;

      addProductImages(doc, productImages, currentX, currentY);
    }
  }

  private createHeaders(doc: jsPDF, product: ProductDetailViewModel) {
    const pageCount: number = doc.getNumberOfPages();
    for (let pageNumber = 2; pageNumber <= pageCount; pageNumber++) {
      doc.setPage(pageNumber);
      let currentX = PAGE_MARGIN;
      let currentY = PAGE_MARGIN;

      addHeader(doc, product, currentX, currentY);
    }
  }

  private createFooters(doc: jsPDF) {
    const pageCount: number = doc.getNumberOfPages();
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      doc.setPage(pageNumber);
      let currentX = 0;
      let currentY = PAGE_HEIGHT - FOOTER_HEIGHT;

      addFooter(doc, pageNumber, pageCount, currentX, currentY);
    }
  }

  private async getProductImages(product: ProductDetailViewModel) {
    const images: ProductImageBase64Response[] = await firstValueFrom(
      this.serviceHandler.get(
        `${environment.imageBase64ApiUrl}?productNumber=${product?.productNumber}`,
      ),
    );

    const contextKeyToImageMap = new Map<string, ProductImageBase64Response>();
    images.forEach(image => {
      contextKeyToImageMap.set(image?.contextKey, image);
    });
    const orderedContextKeys = getOrderedContextKeys(
      product?.summary?.productAssets?.productImages || {},
      getPriorityOrders(product?.summary),
    );

    return orderedContextKeys
      ?.map(contextKey => {
        return contextKeyToImageMap.get(contextKey);
      })
      ?.filter(image => image?.isDownloadSuccessful)
      ?.map(image => image?.imageBase64);
  }
}
